/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useActionMenu.js
 *
 * Purpose: Hook that drives an action-menu component, tracking which action's modal is
 *     active and rendering that modal.
 *
 * Scope: Action menus (e.g. row actions on a list) that each open a distinct modal.
 *
 * Tags: customer-portal
 *
 * Overview: Filters out hidden actions and tracks the active menu item by 1-based index
 *     (0 meaning none active). `renderActionModals` renders only the modal component for
 *     the active action, passing it visibility, a dismiss handler, and any
 *     action-specific modal props. `handleClick` sets the active index and forwards the
 *     click to an optional external handler, letting callers add side effects like
 *     analytics without altering menu behavior.
 *
 * Exports: default (useActionMenu)
 */

import { useState, useMemo } from 'react';

export default function useActionMenu(actions = [], externalHandleMenuItemClick) {
  const visibleActions = useMemo(() => actions.filter(a => !a.hidden), [actions]);
  const [activeIndex, setActiveIndex] = useState(0);
  const onToggle = () => setActiveIndex(0);

  function renderActionModals() {
    return visibleActions.map((action, i) => {
      const C = action.modal;
      if (i + 1 !== activeIndex) return null;
      return (
        <C
          key={i}
          visible={i + 1 === activeIndex}
          onDismiss={onToggle}
          {...action.modalProps}
        />
      );
    });
  }

  const handleClick = (index) => {
    setActiveIndex(index);
    if (typeof externalHandleMenuItemClick === 'function') {
      externalHandleMenuItemClick(index);
    }
  };

  return {
    menuProps: {
      actions: visibleActions,
      handleMenuItemClick: handleClick,
    },
    renderActionModals,
  };
}
